import { BookRecord, EditionRecord, PrintRecord, GroupRecord } from "./Types";
import { BlakeDexie, } from "./BlakeDexie";
import Dexie from "dexie";

/**
 * The expected format of the JSON for an individual palette
 * to be in
 */
interface RawPaletteItem {
    id: string,
    groupID: string,
    swatches: [[number, [number, number, number]]],
    did: string
}

/**
 * The expected format for the entire palette JSON file
 */
interface RawPaletteCollection {
    [plateID: string]: [RawPaletteItem]
}

/**
 * A data source with a url of a JSON file. 
 * url - the raw data source
 * recordCount - the expected record count, to test
 *               whether the data was imported correctly
 * getTable - a method to get the expected table
 * storeRawData - a method to store the raw data
 */
interface DataSource {
    url: string,
    recordCount: number,
    storeRawData: (ctx: BlakeData, data: any) => void,
    getTable: (ctx: BlakeData) => Dexie.Table<any, any>
};

/**
 * A class for initializing a blake database by loading JSON
 * files and storing data in an IndexedDB (via Dexie)
 */
export class BlakeData {

    db: BlakeDexie;
    private static instance: BlakeData;
    private static counter: number;

    dataSources: Array<DataSource> = [{
        url: `${process.env.PUBLIC_URL}/data/book-titles.json`,
        recordCount: 13,
        // table: "books",
        storeRawData: (ctx: BlakeData, data: Array<BookRecord>) => ctx.storeBookTitles(data),
        getTable: (ctx: BlakeData) => ctx.db.books
    }, {
        // table: "editions",
        url: `${process.env.PUBLIC_URL}/data/edition-index.json`,
        recordCount: 100,
        storeRawData: (ctx: BlakeData, data: Array<EditionRecord>) => ctx.storeEditionsIndex(data),
        getTable: (ctx: BlakeData) => ctx.db.editions

    }, {
        // table: "prints",
        url: `${process.env.PUBLIC_URL}/data/new-all-palettes.json`,
        recordCount: 2488,
        storeRawData: (ctx: BlakeData, data: RawPaletteCollection) => ctx.storePlatesNew(data),
        getTable: (ctx: BlakeData) => ctx.db.prints

    },
    ];

    static getInstanceWithCallback(callback: (db?: BlakeData) => void) {
        if (!BlakeData.instance) {
            BlakeData.instance = new BlakeData(callback);
            BlakeData.instance.db.on("blocked", () => {
                console.log("this database is blocked");
            });
        } else {
            callback(BlakeData.instance);
        }
        return BlakeData.instance;
    }

    static getInstance() {
        return BlakeData.getInstanceWithCallback(() => { })
    }


    private constructor(callback: (db?: BlakeData) => void) {

        BlakeData.counter = 0;
        // create the database
        this.db = new BlakeDexie()

        // bind methiods
        this.addData = this.addData.bind(this);
        this.allDataSourcesLoaded = this.allDataSourcesLoaded.bind(this);
        this.dataSourceLoaded = this.dataSourceLoaded.bind(this);
        this.getDexie = this.getDexie.bind(this);
        this.populate = this.populate.bind(this);

        this.storeBookTitles = this.storeBookTitles.bind(this);
        this.storeEditionsIndex = this.storeEditionsIndex.bind(this);
        // this.storePlates = this.storePlates.bind(this);
        this.storePlatesNew = this.storePlatesNew.bind(this);

        this.db.open()
            .then((d: Dexie) => {
            })
            .then(() => this.db.books.clear())
            .then(() => this.db.editions.clear())
            .then(() => this.db.prints.clear())
            .then(() => this.db.groups.clear())
            // .then(() => { console.log("Populating") })
            .then(() => {
                this.populate().then(populated => {
                    if (populated === true) {
                        callback(this)
                    } else {
                        callback();
                    }
                });

            })




    }


    async dataSourceLoaded(ds: DataSource): Promise<boolean> {
        const count = await ds.getTable(this).count();

        const dsLoaded = (count === ds.recordCount);
        return dsLoaded;
    }


    async allDataSourcesLoaded(): Promise<boolean> {
        for (var i = 0; i < this.dataSources.length; i++) {
            const ds: DataSource = this.dataSources[i];
            const dsLoaded = await this.dataSourceLoaded(ds);

            if (!dsLoaded) { return false; }
        };
        return true;
    }

    getDexie(): BlakeDexie {
        return this.db;
    }

    async populate() {


        for (const s of this.dataSources) {
            await this.addData(s);
        }



        // this.dataSources.forEach(async s => {
        //     let ret = await this.addData(s); return ret;}
        // );

        return this.allDataSourcesLoaded();
    }

    async addData(ds: DataSource, force: boolean = false): Promise<any> {

        const alreadyLoaded: boolean = await this.dataSourceLoaded(ds);

        if ((alreadyLoaded === true) && (force === false)) {
            // console.log("Already loaded data source: " + ds.url);
            return;
        }

        return fetch(ds.url)
            .then(d => d.json())
            // .then(d => { console.log("Started adding data", d); return d; })
            .then(d => ds.storeRawData(this, d))
            // .then(res => { console.log("Finished adding data", res) })
            .catch(error => {
                console.error("ERROR LOADING DATA ", error)
            });
    }


    storeEditionsIndex(editions: Array<EditionRecord>): Promise<any> {
        return this.db.editions.bulkAdd(editions)
            .catch(function (reason: any) {
                console.error("Error adding objects: ", reason)
            })
    }

    storeBookTitles(titles: Array<BookRecord>): Promise<any> {
        return this.db.books.bulkAdd(titles)
            .catch(function (reason: any) {
                console.error("Error adding objects: ", reason)
            })

    }

    storePlatesNew(editions: RawPaletteCollection): Promise<any> {

        // console.log("Storing plates", editions)

        // convert palettes to array since the file is
        // an object with edition ids as keys
        // let allPrints: Array<PrintRecord> = [];

        let allPrints: Array<PrintRecord> = [];
        let allGroupsObject: { [groupID: string]: Array<PrintRecord> } = {};

        Object.keys(editions).forEach(editionID => {

            let rawPrints: [RawPaletteItem] = editions[editionID];

            // let prints = rawPrint.map((rawPrint: RawPrint) => {
            // const swatches = rawPrint.swatches;
            let cleanPrints = rawPrints.map((rawPrint: RawPaletteItem) => {

                // console.log("Adding", rawPrint);

                // add print to groupID
                const groupID: string = rawPrint.groupID;
                    // printID = rawPrint.id;

                const ret = {
                    id: rawPrint.id,
                    swatches: rawPrint.swatches,
                    // bids: [`${plate.bid}`],
                    editionID,
                    // dbi: rawPrint.dbi,
                    groupID: rawPrint.groupID,
                    descID: rawPrint.did
                };


                if (!(groupID in allGroupsObject)) {
                    allGroupsObject[groupID] = []
                }

                allGroupsObject[groupID].push(ret)

                return ret;


            })


            allPrints = allPrints.concat(cleanPrints);

        });

        let allGroups: Array<GroupRecord> = Object.keys(allGroupsObject).map(groupID => {
            const ret = {
                id: groupID,
                prints: allGroupsObject[groupID]
            }

            // we could use session storage to accelerate this on safari
            sessionStorage.setItem(`yaledh-blake-group-${groupID}`, JSON.stringify(ret));

            return ret;
        })


        this.db.groups.bulkAdd(allGroups)
            .catch(function (reason: any) {
                console.error("Error adding objects: ", reason)
            })

        return this.db.prints.bulkAdd(allPrints)
            .catch(function (reason: any) {
                console.error("Error adding objects: ", reason)
            })


    }



}